import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Loader2, AlertTriangle, BookOpen } from 'lucide-react';

// Configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://127.0.0.1:5001/jangal-pro-1b256/us-central1/chat'
    : '/api/chat');
// IMPORTANT : Limiter l'historique envoyé à l'API réduit drastiquement la consommation de tokens.
// 10 messages (5 échanges) suffisent généralement pour garder le contexte.
const MAX_HISTORY_LENGTH = 10; 

const AIChatAssistant = ({ schoolId, userName = 'Élève' }) => {
    const [messages, setMessages] = useState([
        {
            role: 'ai',
            text: `Bonjour ${userName} ! Je suis votre assistant scolaire IA. Je suis là pour vous aider à comprendre vos leçons. Quelle est votre question aujourd'hui ?`,
            sources: []
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Prompt système optimisé pour économiser des tokens tout en gardant la personnalité
    const systemPrompt = `Tu es un tuteur IA pour l'école ${schoolId}. Règles: 1. Ne donne JAMAIS la réponse finale directement. 2. Guide pas à pas. 3. Sois bref et encourageant. 4. Langue: Français.`;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages]);

    // Fonction fetch simplifiée et robuste
    const sendMessageToBackend = async (payload) => {
        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // Gestion spécifique du Rate Limit (429) fréquent en version gratuite
                if (response.status === 429) {
                    throw new Error("L'assistant est très sollicité (Quota gratuit atteint). Attendez une minute.");
                }
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userText = input.trim();
        const newMessages = [...messages, { role: 'user', text: userText, sources: [] }];
        
        setMessages(newMessages);
        setInput('');
        setIsLoading(true);

        try {
            // 1. OPTIMISATION : On ne garde que les N derniers messages pour l'API
            // On exclut les messages d'erreur pour ne pas polluer le contexte
            const validMessages = newMessages.filter(m => m.role !== 'error');
            const recentHistory = validMessages.slice(-MAX_HISTORY_LENGTH).map(msg => ({
                role: msg.role === 'user' ? 'user' : 'model',
                parts: [{ text: msg.text }]
            }));

            // 2. Préparation du payload
            const payload = {
                history: recentHistory,
                systemInstruction: systemPrompt,
                // On peut passer le modèle souhaité au backend si celui-ci est dynamique
                model: 'gemini-1.5-flash' 
            };

            const data = await sendMessageToBackend(payload);
            const candidate = data.candidates?.[0];

            if (candidate?.content?.parts?.[0]?.text) {
                const aiText = candidate.content.parts[0].text;
                
                // Gestion des sources (Grounding)
                let sources = [];
                const grounding = data.promptFeedback?.groundingMetadata || candidate.groundingMetadata;
                if (grounding?.groundingAttributions) {
                    sources = grounding.groundingAttributions
                        .filter(src => src.uri && src.title)
                        .map(src => ({ uri: src.uri, title: src.title }));
                }

                setMessages(prev => [...prev, { role: 'ai', text: aiText, sources }]);
            } else {
                throw new Error("Pas de réponse générée.");
            }

        } catch (error) {
            console.error("Erreur Chat:", error);
            setMessages(prev => [
                ...prev,
                {
                    role: 'error',
                    text: `Oups ! ${error.message}`,
                    sources: []
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    // Rendu des messages (UI légèrement nettoyée)
    const renderMessage = (msg, index) => {
        const isUser = msg.role === 'user';
        const isError = msg.role === 'error';

        return (
            <div key={index} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-fadeIn`}>
                <div className={`max-w-[85%] sm:max-w-[75%] p-4 rounded-2xl shadow-sm ${
                    isUser ? 'bg-indigo-600 text-white rounded-br-none' : 
                    isError ? 'bg-red-50 text-red-600 border border-red-200' : 
                    'bg-white border border-gray-100 text-gray-800 rounded-tl-none'
                }`}>
                    {isError && <AlertTriangle className="w-5 h-5 mb-2 inline-block mr-2" />}
                    <div className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed">
                        {msg.text}
                    </div>

                    {/* Affichage des sources amélioré */}
                    {msg.sources && msg.sources.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-100/20">
                            <div className="flex items-center text-xs opacity-80 mb-1">
                                <BookOpen className="w-3 h-3 mr-1" />
                                <span>Sources vérifiées :</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {msg.sources.map((src, idx) => (
                                    <a key={idx} href={src.uri} target="_blank" rel="noopener noreferrer" 
                                       className={`text-xs px-2 py-1 rounded-full truncate max-w-[150px] transition-colors ${
                                           isUser ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                                       }`}>
                                        {src.title}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-[600px] w-full max-w-3xl mx-auto bg-gray-50 rounded-xl shadow-xl overflow-hidden font-sans border border-gray-200">
            {/* Header */}
            <div className="bg-white p-4 border-b border-gray-200 flex items-center justify-between shadow-sm z-10">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-100 p-2 rounded-lg">
                        <MessageSquare className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-800">Tuteur Assistant</h3>
                        <p className="text-xs text-gray-500">Propulsé par Gemini 1.5 Flash</p>
                    </div>
                </div>
            </div>

            {/* Zone de messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                {messages.map(renderMessage)}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Zone */}
            <div className="p-4 bg-white border-t border-gray-200">
                <form onSubmit={handleSendMessage} className="relative flex items-center gap-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Pose ta question sur le cours..."
                        className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        disabled={isLoading}
                    />
                    <button
                        type="submit"
                        disabled={isLoading || !input.trim()}
                        className="absolute right-2 p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                </form>
                <div className="text-center mt-2">
                    <span className="text-[10px] text-gray-400">L'IA peut faire des erreurs. Vérifiez les informations importantes.</span>
                </div>
            </div>
        </div>
    );
};

export default AIChatAssistant;